import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_DOCUMENT_CLAUSES_QUERY } from './queries';
import { DOCUMENT_CLAUSE_DELETED } from './subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import ExportAction from './actions/ExportAction';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_DOCUMENT_CLAUSE } from './mutations';
import DocumentClauseItem from './DocumentClauseItem';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import useSubscription from 'hooks/apollo/useSubscription';

export const documentClauseStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'clauseCode',
      caption: 'Clause.ClauseCode',
      type: 'text'
    },
    {
      name: 'clauseText',
      caption: 'Clause.ClauseText',
      type: 'text'
    },
    {
      name: 'parentClause',
      caption: 'Clause.ParentClauseText',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'Clause.ClauseText',
      value: 'ClauseText'
    },
    {
      caption: 'Clause.ClauseCode',
      value: 'ClauseCode'
    },
    {
      caption: 'Clause.ParentClauseText',
      value: 'Clauses.ClauseText'
    }
  ],
  pager: {
    orderBy: 'ClauseText',
    direction: 'asc'
  }
});

const DocumentClauseListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const confirm = useConfirmDialog();
  const client = useApolloClient();
  const [activeService] = useContext(ServiceContext);
  const { addActions } = useListBrowserUtils(documentClauseStore);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_DOCUMENT_CLAUSES_QUERY,
        name: 'pagedDocumentClauses'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(DOCUMENT_CLAUSE_DELETED, {
    onData: data => {
      addToast(t('Clause.ClauseDeleted'), 'success');
      client.cache.evict({ id: `DocumentClauseItem:{"clauseId":${data.data.data.documentClauseDeleted.clauseId}}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedDocumentClauses' });
      client.cache.gc();
    }
  });

  const [deleteDocumentClause] = useMutation(DELETE_DOCUMENT_CLAUSE);

  const handleDelete = useCallback(
    clauseId => {
      confirm(
        () => {
          deleteDocumentClause({ variables: { input: { clauseId } } });
        },
        {
          content: t('Clause.DeleteClause')
        }
      );
    },
    [deleteDocumentClause, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedDocumentClauses' });
      client.cache.gc();
    }
  }, [activeService, client]);

  useEffect(() => {
    addActions([
      {
        name: 'export',
        icon: <ExportAction />
      }
    ]);
  }, [addActions, t]);

  const itemComponent = useCallback(({ data }) => <DocumentClauseItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'orderNo', caption: 'Clause.OrderNo', width: '100px' },
      { name: 'clauseCode', caption: 'Clause.ClauseCode' },
      { name: 'clauseText', caption: 'Clause.ClauseText' },
      { name: 'formula', caption: 'Clause.Formula' },
      { name: 'parentClauseText', caption: 'Clause.ParentClauseText' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='document-clause-browser' store={documentClauseStore} ItemComponent={itemComponent} columns={columns} />
      <ApolloFetchingComponent store={documentClauseStore} options={fetchingOptions} />
    </Card>
  );
};

export default DocumentClauseListBrowser;
